// packages/shared/src/schemas/observation.schema.ts
//
// The provider contract. This is what a SkinAnalysisProvider must return, and
// the boundary the whole determinism argument rests on: severities in, scores
// computed here. A provider that returns a number instead of an observation
// fails validation rather than quietly reaching the report.
//
// The metric and flag shapes are built from METRICS and ANATOMY_FLAGS, so
// adding a metric extends this schema automatically. Adding it in two places is
// how a metric ends up observed but never scored.

import { z } from 'zod';
import { METRIC_KEYS, ANATOMY_FLAG_KEYS, PRE_NORMALISATION_METRICS } from '../metrics';
import type { AnatomyFlagKey, MetricKey } from '../metrics';

export const severitySchema = z
  .number()
  .int()
  .min(0)
  .max(4)
  .describe('0 none · 1 minimal · 2 mild · 3 moderate · 4 marked');

export const faceRegionSchema = z.enum([
  'forehead',
  'glabella',
  'nose',
  'leftCheek',
  'rightCheek',
  'periorbital',
  'infraorbital',
  'upperEyelid',
  'lowerEyelid',
  'nasolabial',
  'perioral',
  'chin',
  'jawline',
  'tzone',
  'fullFace',
]);

export const lateralitySchema = z.enum(['left', 'right', 'bilateral']);

export const observationSchema = z.object({
  severity: severitySchema,
  // Empty is a valid answer: a metric with nothing to see — no acne, no spots —
  // has nowhere to point. Requiring one region discarded exactly those passes,
  // and a clear-skinned face could lose all three and fail the scan.
  regions: z.array(faceRegionSchema),
  coveragePct: z.number().min(0).max(100),
  confidence: z.number().min(0).max(1),
  evidence: z.string().transform((v) => v.slice(0, 240)).optional(),
});

/**
 * Anatomy findings have no coverage — an eyelid does not have one — and do have
 * laterality, because an asymmetric finding is clinically different from a
 * symmetric one.
 */
export const anatomyFlagObservationSchema = z.object({
  severity: severitySchema,
  laterality: lateralitySchema,
  confidence: z.number().min(0).max(1),
  evidence: z.string().transform((v) => v.slice(0, 240)).optional(),
});

/**
 * Whether the photo can be analysed at all. A provider saying "no" is a better
 * outcome than a provider guessing, and the widget turns this into a retake
 * prompt with a specific fix.
 */
export const faceQualitySchema = z.object({
  usable: z.boolean(),
  lighting: z.enum(['adequate', 'dark', 'bright', 'uneven']),
  faceCount: z.number().int().min(0).max(10).optional(),
  occlusion: z.array(z.enum(['glasses', 'hair', 'hand', 'mask', 'none'])).optional(),
  notes: z.string().transform((v) => v.slice(0, 240)).optional(),
});

/**
 * Phototype is the one reading the provider supplies rather than the scoring
 * engine computing it, because it is an observation. It is also the most
 * safety-relevant field here: types IV-VI carry PIH risk and gate every
 * energy-based treatment.
 */
export const fitzpatrickSchema = z.object({
  type: z.number().int().min(1).max(6),
  confidence: z.number().min(0).max(1),
});

// Built from the key lists so a new metric extends the schema automatically.
// `.partial()` on the object below is what makes each entry optional, so the
// shapes here are the bare schemas.
const observationsShape = Object.fromEntries(
  METRIC_KEYS.map((key) => [key, observationSchema]),
) as Record<MetricKey, typeof observationSchema>;

const anatomyFlagsShape = Object.fromEntries(
  ANATOMY_FLAG_KEYS.map((key) => [key, anatomyFlagObservationSchema]),
) as Record<AnatomyFlagKey, typeof anatomyFlagObservationSchema>;

export const providerResponseSchema = z.object({
  faceQuality: faceQualitySchema,
  fitzpatrick: fitzpatrickSchema,
  observations: z.object(observationsShape).partial(),
  anatomyFlags: z.object(anatomyFlagsShape).partial().optional(),
  /** Age estimated from ageing signals. Used only when the patient skipped the question. */
  estimatedAge: z.number().int().min(10).max(100).optional(),
});

export type ProviderResponse = z.infer<typeof providerResponseSchema>;
export type ObservationPayload = z.infer<typeof observationSchema>;
export type AnatomyFlagPayload = z.infer<typeof anatomyFlagObservationSchema>;
export type FaceQuality = z.infer<typeof faceQualitySchema>;

/**
 * Metrics the provider must read from the pre-normalised image.
 *
 * Preprocessing flattens uneven luminance so scores are comparable across
 * bathrooms, and tear trough depth and eye bag protrusion are visible only as a
 * shadow gradient. Reading them off the normalised buffer under-detects them
 * with no error to notice — just quietly low severities.
 */
export const PRE_NORMALISATION_KEYS: readonly MetricKey[] = PRE_NORMALISATION_METRICS;

/**
 * Provider output is not trusted. A pass that fails validation is discarded and
 * the median is taken over the survivors, rather than letting one malformed
 * response poison the scan — which is the whole reason for running three.
 */
export function parseProviderResponse(raw: unknown): ProviderResponse | null {
  const result = providerResponseSchema.safeParse(raw);
  return result.success ? result.data : null;
}
